
import { Color } from "@mui/material";
import { Timestamp } from "firebase/firestore";

// Define a type for IDs, which can be either a string or a number
export type Id = string | number;

// Create the context with default values
export interface UserDetails {
  uid: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  bio: string;
  professions: string[];
  roles: string[];
  createdAt: Timestamp;
  profileImageUrl: string;
  eventRoles: EventRoles[];
  accountType: AccountType;
}

export interface EventRoles {
  eventId: string;
  roles: RolesForEvent[];
}

export enum RolesForEvent {
  SUBMITTER = "submitter",
  PRESENTER = "presenter",
  REVIEWER = "reviewer",
  VOTER = "voter",
  MANAGER = "manager",
}

export enum AccountType {
  COMMUNITY = "community",
  VOLUNTEER = "volunteer",
  ADMIN = "admin",
}

// Define an interface for the Brand object
export interface Brand {
  name: string;
  logoUrl: string;
  faviconUrl: string;
}

// Define an interface for the Event object (ReEvent because Event is a reserved word)
export interface ReEvent {
  id: Id;

  //Basic information about the event
  name: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;

  //relations
  sessions: Id[];
  eventConfiguration: EventConfiguration;

}

// Define an interface for the EventConfiguration object
export interface EventConfiguration {
  dashboardTitle: string;
  dashboardWelcomeText: string;
  dashboardPictureUrl: string;
  logoUrl: string;
  faviconUrl: string;
  websiteUrl: string;
  targetAudience: string;
  submissionStartDate: Date | null;
  submissionEndDate: Date | null;
  votingStartDate: Date | null;
  votingEndDate: Date | null;
  programDate: Date | null;
  hasCustomStyling: boolean;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  warningColor: string;
  infoColor: string;
  errorColor: string;
  linkColor: string;
  clickedLinkColor: string;
  topics: Topic[];
  roomSetups: RoomSetup[];
  possibleDurations: possibleDuration[];
  useXpFactor: boolean;
  areReviewsAndCommentsVisible: boolean;
  isReviewCreationActive: boolean;
  isCommentCreationActive: boolean;
}

export interface Topic{
  id: Id;
  name: string;
  color: string;
}
export interface RoomSetup{
  id: Id;
  name: string;
}

export interface possibleDuration{
  id: Id;
  duration: number;
}

// Define an interface for the Session object (ReSession becuase Session is a reserved word)
export interface ReSession {
  id: Id;
  title: string;
  subtitle: string;
  shortDescription: string;
  longDescription: string;
  topic: Id;
  type: SessionType;
  status: SessionStatus;
  duration: Id;
  experienceLevel: ExperienceLevel;
  intendedAudience: string;
  laptopsRequired: boolean;
  materialDescription: string;
  materialUrl: string;
  materialsNeeded: string;
  maxParticipants: number;
  otherLimitations: string;
  outlineOrTimetable: string;
  roomSetup: Id;
  sessionGoal: string;
  xpFactor: number;
  createdAt: Date | null;
  createdBy: Id;

  updatedAt: Date | null;
  updatedBy: Id;
  presenterIds: Id[];
}

export interface Review{
  id: Id;
  sessionId: Id;
  isNew: boolean;
  improvementOpportunities: string;
  whatIlike: string;
  xpFactor: number;
  score: number;
  createdAt: Date;
  createdBy: Id;
}

export interface Comment{
  id: Id;
  reviewId: Id;
  isNew: boolean;
  content: string;
  createdAt: Date;
  createdBy: Id;
}

export enum SessionType {
  HANDS_ON = "hands on",
  DISCOVERY_SESSION = "discovery session",
  EXPERIENTIAL_LEARNING_SESSION = "experiential learning session",
  SHORT_EXPERIENCE_REPORT = "short experience report"
}

export enum SessionStatus {
  DRAFT = "Draft",
  CANCELLED = "Cancelled",
  CONFIRMED = "Confirmed",
}

export enum ExperienceLevel {
  NO_EXPERIENCE = "No experience",
  BEGINNER = "Beginner",
  INTERMEDIATE = "Intermediate",
  ADVANCED = "Advanced",
  EXPERT = "Expert"
}

//session-version for scheduler
export interface EventSession {
  id: Id;
  orderId: number;
  roomId: Id;
  isBreak: boolean;
  isCopy: boolean;
  title: string;
  duration: Id;
  startTime: Date;
  endTime: Date;
}

export interface Room {
  id: Id;
  name: string;
  capacity: number;
}
